import type {
  AddFavoriteSuccessResponse,
  PatternDetailsResponse,
  PatternVersionResponse,
  RemoveFavoriteSuccessResponse,
  CreateTempPatternResponse,
  PatternTempResponse,
  UpdateTempPatternRequest,
  DeleteTempPatternResponse,
  CreateDraftPatternResponse,
  FetchPatternDraftResponse,
  UpdateDraftPatternResponse,
  PublishPatternResponse,
  RepublishPatternResponse,
  MigrateTempImagesResponse,
  TempToDraftMigrationRequest,
  TempToDraftMigrationResponse,
  PatternNotesResponse,
  CreateNoteResponse,
  GetUserAppealsResponse,
  ArchivePatternResponse,
  UnarchivePatternResponse,
  CreateAppealResponse,
  CheckSubmissionLimitResponse,
  SuccessResponse,
  ValidateMaterialArchiveResponse,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

/**
 * The AUTHENTICATED pattern read — `GET /api/secure/pattern/{patternId}`
 * (content-visibility-model, Req 4.8, task 17).
 *
 * Answers the SAME `PatternDetailsResponse` contract as the public
 * `GET /api/patterns/{id}/details`, for a caller whose identity API Gateway has verified. The handler is
 * `lambda-secure/src/routes/patterns/get-pattern-authenticated.ts`; it applies the shared read gate with
 * the verified identity and the moderator claim, so an Owner or a Moderator resolves a `private` pattern
 * 200 while a third party gets the same 404 a nonexistent id produces.
 *
 * NOT a replacement for the public fetcher and NOT to be called first. It is the SECOND attempt of
 * `usePublicPatternQuery`'s fallback and nothing else calls it: routing signed-in traffic here would make
 * every pattern page uncacheable to serve a rare case. See `src/queries/authenticatedFallback.ts`.
 *
 * `opts.signal` is React Query's cancellation signal, threaded so an abandoned navigation is a
 * CANCELLATION rather than a query error — `secureFetch` passes it to both the initial request and its
 * 401 refresh retry.
 */
export async function getPatternAuthenticated(
  patternId: string,
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<PatternDetailsResponse> {
  return secureFetch<PatternDetailsResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}`,
    idToken,
    {
      method: 'GET',
      ...(opts?.signal ? { signal: opts.signal } : {}),
    },
  )
}

/**
 * The AUTHENTICATED version read — `GET /api/secure/pattern/{patternId}/version/{versionNumber}`
 * (content-visibility-model, Req 4.8, task 46).
 *
 * Answers `PatternVersionResponse` — the SAME type the public `GET /api/pattern/{id}/version/{n}` answers,
 * not a secure variant of it. That is what lets `usePatternVersionQuery` fall back on a 404 with no
 * adapter: the superseded `SecurePatternVersionResponse` omitted assembled `materials` and its `versions`
 * entries lacked the `published_date` alias the page's merge reads (BL-253), so the two shapes are now one
 * type held by the compiler in both packages.
 *
 * The handler is `lambda-secure/src/routes/patterns/get-pattern-version-authenticated.ts`. It applies the
 * shared read gate to the PATTERN's display record — access is a property of the pattern, not of one of
 * its versions — so an Owner or a Moderator resolves an older version of a `private` pattern while a third
 * party gets the same 404 a nonexistent version produces.
 *
 * NOT to be called first, for the same reason as `getPatternAuthenticated` above: it is the SECOND attempt
 * of the version query's fallback, and routing signed-in traffic here would make the version payload
 * uncacheable to serve a rare case. See `src/queries/authenticatedFallback.ts`.
 */
export async function getPatternVersionAuthenticated(
  patternId: string,
  versionNumber: number,
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<PatternVersionResponse> {
  return secureFetch<PatternVersionResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/version/${encodeURIComponent(String(versionNumber))}`,
    idToken,
    {
      method: 'GET',
      ...(opts?.signal ? { signal: opts.signal } : {}),
    },
  )
}

// Favorite/Unfavorite
export async function addFavoritePattern(patternId: string, idToken: string): Promise<AddFavoriteSuccessResponse> {
  return secureFetch<AddFavoriteSuccessResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/favorite`,
    idToken,
    {
      method: 'POST',
    },
  )
}

export async function removeFavoritePattern(
  patternId: string,
  idToken: string,
): Promise<RemoveFavoriteSuccessResponse> {
  return secureFetch<RemoveFavoriteSuccessResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/favorite`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

// Temp Pattern
export async function createTempPattern(idToken: string): Promise<CreateTempPatternResponse> {
  return secureFetch<CreateTempPatternResponse>(`${SECURE_API_BASE}/pattern/temp`, idToken, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function fetchTempPattern(patternId: string, idToken: string): Promise<PatternTempResponse> {
  return secureFetch<PatternTempResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'GET',
  })
}

export async function updateTempPattern(
  patternId: string,
  tempData: UpdateTempPatternRequest,
  idToken: string,
): Promise<PatternTempResponse> {
  return secureFetch<PatternTempResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'PUT',
    body: JSON.stringify(tempData),
  })
}

export async function deleteTempPattern(patternId: string, idToken: string): Promise<DeleteTempPatternResponse> {
  return secureFetch<DeleteTempPatternResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

// uploadTempPatternImage available via secure/images.ts

// Draft Pattern
export async function createDraftPattern(
  idToken: string,
  data: Record<string, unknown> = {},
): Promise<CreateDraftPatternResponse> {
  return secureFetch<CreateDraftPatternResponse>(`${SECURE_API_BASE}/pattern/draft`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function fetchPatternDraft(patternId: string, idToken: string): Promise<FetchPatternDraftResponse> {
  return secureFetch<FetchPatternDraftResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/draft`,
    idToken,
    {
      method: 'GET',
    },
  )
}

export async function updateDraftPattern(
  patternId: string,
  draftData: unknown,
  idToken: string,
): Promise<UpdateDraftPatternResponse> {
  return secureFetch<UpdateDraftPatternResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/draft`,
    idToken,
    {
      method: 'PUT',
      body: JSON.stringify(draftData),
    },
  )
}

// Publish/Unpublish
export async function publishPattern(patternId: string, idToken: string): Promise<PublishPatternResponse> {
  return secureFetch<PublishPatternResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/publish`,
    idToken,
    {
      method: 'POST',
    },
  )
}

export async function republishPattern(
  patternId: string,
  idToken: string,
  changeNotes: string,
): Promise<RepublishPatternResponse> {
  return secureFetch<RepublishPatternResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/republish`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ change_notes: changeNotes }),
    },
  )
}

export async function unpublishPattern(patternId: string, idToken: string): Promise<void> {
  return secureFetch<void>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/unpublish`, idToken, {
    method: 'POST',
  })
}

// uploadPatternImage available via secure/images.ts

// Image Migration
export async function migrateTempImages(
  draftPatternId: string,
  tempPatternId: string,
  idToken: string,
): Promise<MigrateTempImagesResponse> {
  return secureFetch<MigrateTempImagesResponse>(
    `${SECURE_API_BASE}/pattern/${encodeURIComponent(draftPatternId)}/migrate-images`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ tempPatternId }),
    },
  )
}

export async function migrateTempToDraft(
  tempPatternId: string,
  idToken: string,
): Promise<TempToDraftMigrationResponse> {
  try {
    const requestData: TempToDraftMigrationRequest = { tempPatternId }
    const result = await secureFetch<TempToDraftMigrationResponse>(
      `${SECURE_API_BASE}/pattern/temp-to-draft`,
      idToken,
      {
        method: 'POST',
        body: JSON.stringify(requestData),
      },
    )

    localStorage.removeItem('tempPatternId')

    return result
  } catch (error: unknown) {
    console.error('Failed to migrate temp to draft:', error)
    throw new Error('Failed to save pattern as draft')
  }
}

// Archive
export async function archivePattern(
  patternId: string,
  reason: string | undefined,
  idToken: string,
): Promise<ArchivePatternResponse> {
  return secureFetch<ArchivePatternResponse>(
    `${SECURE_API_BASE}/patterns/${encodeURIComponent(patternId)}/archive`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    },
  )
}

export async function unarchivePattern(patternId: string, idToken: string): Promise<UnarchivePatternResponse> {
  return secureFetch<UnarchivePatternResponse>(
    `${SECURE_API_BASE}/patterns/${encodeURIComponent(patternId)}/unarchive`,
    idToken,
    {
      method: 'POST',
    },
  )
}

// Moderation Notes
export async function fetchPatternNotes(patternId: string, idToken: string): Promise<PatternNotesResponse> {
  return secureFetch<PatternNotesResponse>(
    `${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/notes`,
    idToken,
    {
      method: 'GET',
    },
  )
}

export async function createPatternNote(
  patternId: string,
  content: string,
  category: string = 'other',
  severity: string = 'medium',
  idToken: string,
): Promise<CreateNoteResponse> {
  return secureFetch<CreateNoteResponse>(
    `${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/notes`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ content, category, severity }),
    },
  )
}

// Appeals & Submission Limits
/**
 * `POST /appeal/{contentType}/{contentId}`.
 *
 * **`contentId` MUST be percent-encoded, and the reason is not stylistic.** A REVIEW's appeal address is
 * the composite `{patternId}#{reviewerId}` (`NotificationAppealSection`, BF-074), so it contains a `#`.
 * Interpolated raw, that `#` is a URL FRAGMENT: the browser never puts it on the wire, and the request
 * arrives as `POST /appeal/review/{patternId}` with the reviewer half silently gone. The appeal is then
 * created against the bare pattern id — a 200 with an `appeal_id`, so nothing surfaces the loss — and it
 * collides with any pattern appeal on the same pattern, because `findUnresolvedAppeals` narrows on
 * `content_id` alone. Measured: a local server sees `/appeal/review/pat-1%23rev-2` encoded and
 * `/appeal/review/pat-1` unencoded.
 *
 * The backend half is `appeal-system.createAppeal`, which decodes before use — the same pairing
 * `review-moderation.ts` already carries for the identical composite (BF-066). Both halves are required:
 * encoding alone stores a literal `%23`, decoding alone never receives the second half at all.
 */
export async function createAppeal(
  contentType: string,
  contentId: string,
  message: string,
  idToken: string,
): Promise<CreateAppealResponse> {
  return secureFetch<CreateAppealResponse>(
    `${SECURE_API_BASE}/appeal/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ message }),
    },
  )
}

export async function getUserAppeals(idToken: string): Promise<GetUserAppealsResponse> {
  return secureFetch<GetUserAppealsResponse>(`${SECURE_API_BASE}/appeals`, idToken, {
    method: 'GET',
  })
}

export async function checkSubmissionLimit(
  contentType: string,
  contentId: string,
  idToken: string,
): Promise<CheckSubmissionLimitResponse> {
  return secureFetch<CheckSubmissionLimitResponse>(
    `${SECURE_API_BASE}/submission-limit/${contentType}/${contentId}`,
    idToken,
    {
      method: 'GET',
    },
  )
}

export async function recordSubmission(
  contentType: string,
  contentId: string,
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch<SuccessResponse>(`${SECURE_API_BASE}/submission`, idToken, {
    method: 'POST',
    body: JSON.stringify({ contentType, contentId }),
  })
}

// Material Archive Validation (pattern-related)
export async function validateMaterialArchive(
  materialId: string,
  idToken: string,
): Promise<ValidateMaterialArchiveResponse> {
  return secureFetch<ValidateMaterialArchiveResponse>(
    `${SECURE_API_BASE}/craft-room/material/${encodeURIComponent(materialId)}/archive-validation`,
    idToken,
    {
      method: 'GET',
    },
  )
}
